import React, { useState } from 'react';
import { onboardingApi, AdvertisingProfileData } from '../services/api';
import '../styles/register.css';

interface AdvertisingProfileFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const INTERESTS = [
  'Technologie', 'Business', 'Marketing', 'Design', 'Développement', 'Productivité',
  'Formation', 'E-commerce', 'Finance', 'Santé', 'Sport', 'Voyage',
];

const GENDER_OPTIONS: { value: NonNullable<AdvertisingProfileData['advertising_gender']>; label: string }[] = [
  { value: 'male', label: 'Homme' },
  { value: 'female', label: 'Femme' },
  { value: 'other', label: 'Autre' },
  { value: 'prefer_not_to_say', label: 'Préfère ne pas répondre' },
];

// Formulaire extrait de l'ancien parcours d'inscription (RegisterPage/StepPlanSelection) :
// désormais proposé depuis la modal de complétion de profil plutôt qu'imposé à l'inscription.
//
// Le consentement publicitaire reste facultatif.
export const AdvertisingProfileForm: React.FC<AdvertisingProfileFormProps> = ({ onSuccess, onCancel }) => {
  const [consent, setConsent] = useState(false);
  const [interests, setInterests] = useState<string[]>([]);
  const [pcsLevel1, setPcsLevel1] = useState('');
  const [pcsLevel2, setPcsLevel2] = useState('');
  const [age, setAge] = useState<number | ''>('');
  const [gender, setGender] = useState<AdvertisingProfileData['advertising_gender'] | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleInterest = (interest: string) => {
    setInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest].slice(0, 3)
    );
  };

  const handleConsentChange = (checked: boolean) => {
    setConsent(checked);
    setError(null);
    if (!checked) {
      setInterests([]);
      setPcsLevel1('');
      setPcsLevel2('');
      setAge('');
      setGender('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (consent) {
      if (interests.length !== 3) {
        setError('Veuillez sélectionner 3 centres d\'intérêt publicitaires');
        return;
      }
      if (!pcsLevel1) {
        setError('Veuillez sélectionner votre catégorie socio-professionnelle');
        return;
      }
      if (!age) {
        setError('Veuillez sélectionner votre tranche d\'âge');
        return;
      }
      if (!gender) {
        setError('Veuillez sélectionner votre sexe');
        return;
      }
    }

    setSubmitting(true);
    try {
      await onboardingApi.completeAdProfile(
        consent
          ? {
              advertising_consent: true,
              advertising_interests: interests,
              pcs_level_1: pcsLevel1,
              pcs_level_2: pcsLevel2 || undefined,
              advertising_age: age as number,
              advertising_gender: gender as AdvertisingProfileData['advertising_gender'],
            }
          : { advertising_consent: false }
      );
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Une erreur est survenue lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="advertising-profile-form" onSubmit={handleSubmit}>
      <p className="form-intro">
        Le plan gratuit affiche des publicités. Vous pouvez choisir de nous transmettre quelques
        informations (centres d'intérêt, catégorie socio-professionnelle, tranche d'âge, sexe)
        afin de recevoir des publicités plus pertinentes. Vous pouvez aussi refuser et valider ce
        formulaire tel quel : le plan gratuit reste accessible et vous verrez alors des
        publicités non personnalisées.
      </p>
      <p className="form-intro">
        Ces données sont traitées en interne par Ventilio uniquement à des fins publicitaires.
        Elles ne sont ni vendues ni partagées avec des annonceurs. Pour en savoir plus, consultez
        notre <a href="/advertising-policy" target="_blank" rel="noreferrer">Politique de publicité</a>.
      </p>

      {error && <div className="error-message">{error}</div>}

      <label className="consent-item">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => handleConsentChange(e.target.checked)}
        />
        <span>
          J'accepte la collecte de mes données pour la personnalisation des publicités affichées
          dans Ventilio, conformément à la <a href="/advertising-policy" target="_blank" rel="noreferrer">Politique de publicité</a>.
        </span>
      </label>

      {consent && (
        <>
          <div className="form-group">
            <label>3 principaux centres d'intérêt publicitaires *</label>
            <p className="field-help">Sélectionnez les thématiques qui vous intéressent le plus</p>
            <div className="interests-grid">
              {INTERESTS.map(interest => (
                <label key={interest} className="interest-checkbox">
                  <input
                    type="checkbox"
                    value={interest}
                    checked={interests.includes(interest)}
                    onChange={() => toggleInterest(interest)}
                    disabled={!interests.includes(interest) && interests.length >= 3}
                  />
                  <span>{interest}</span>
                </label>
              ))}
            </div>
            <small className="selected-count">{interests.length}/3 sélectionnés</small>
          </div>

          <div className="form-group">
            <label>Catégorie socio-professionnelle (Niveau 1) *</label>
            <select
              value={pcsLevel1}
              onChange={(e) => { setPcsLevel1(e.target.value); setPcsLevel2(''); }}
              required
            >
              <option value="">Sélectionnez...</option>
              <option value="1000">Agriculteurs</option>
              <option value="2000">Artisans, commerçants et chefs d'entreprise</option>
              <option value="3000">Cadres</option>
              <option value="4000">Professions intermédiaires</option>
              <option value="5000">Employés</option>
              <option value="6000">Ouvriers</option>
            </select>
          </div>

          {pcsLevel1 && (
            <div className="form-group">
              <label>Catégorie (Niveau 2) *</label>
              <select value={pcsLevel2} onChange={(e) => setPcsLevel2(e.target.value)} required>
                <option value="">Sélectionnez...</option>
                {pcsLevel1 === '1000' && (
                  <option value="1000">Exploitants de l'agriculture, sylviculture, pêche et aquaculture</option>
                )}
                {pcsLevel1 === '2000' && (
                  <>
                    <option value="2100">Artisans</option>
                    <option value="2200">Commerçants et assimilés</option>
                    <option value="2300">Chefs d'entreprise de plus de 10 personnes</option>
                  </>
                )}
                {pcsLevel1 === '3000' && (
                  <>
                    <option value="3100">Professions libérales</option>
                    <option value="3300">Cadres de la fonction publique</option>
                    <option value="3400">Professeurs et professions scientifiques</option>
                    <option value="3500">Professions de l'information, de l'art et des spectacles</option>
                    <option value="3700">Cadres administratifs et commerciaux</option>
                    <option value="3800">Cadres techniques d'entreprise</option>
                  </>
                )}
                {pcsLevel1 === '4000' && (
                  <>
                    <option value="4200">Professions de l'enseignement primaire et professionnel</option>
                    <option value="4300">Intermédiaires de la santé et du travail social</option>
                    <option value="4400">Religieux</option>
                    <option value="4500">Intermédiaires de la fonction publique</option>
                    <option value="4600">Intermédiaires des entreprises</option>
                    <option value="4700">Techniciens</option>
                    <option value="4800">Agents de maîtrise de production</option>
                  </>
                )}
                {pcsLevel1 === '5000' && (
                  <>
                    <option value="5200">Employés de la fonction publique</option>
                    <option value="5300">Policiers, militaires et agents de sécurité privée</option>
                    <option value="5400">Employés administratifs d'entreprise</option>
                    <option value="5500">Employés de commerce</option>
                    <option value="5600">Personnels des services directs aux particuliers</option>
                  </>
                )}
              </select>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Âge *</label>
              <select
                value={age}
                onChange={(e) => setAge(e.target.value ? Number(e.target.value) : '')}
                required
              >
                <option value="">Sélectionnez...</option>
                <option value="18">18-24 ans</option>
                <option value="25">25-34 ans</option>
                <option value="35">35-44 ans</option>
                <option value="45">45-54 ans</option>
                <option value="55">55-64 ans</option>
                <option value="65">65 ans et plus</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Sexe *</label>
            <div className="gender-options">
              {GENDER_OPTIONS.map(option => (
                <label key={option.value} className="radio-option">
                  <input
                    type="radio"
                    name="advertising_gender"
                    value={option.value}
                    checked={gender === option.value}
                    onChange={() => setGender(option.value)}
                    required
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="privacy-notice">
        {consent
          ? 'Ces informations sont utilisées uniquement pour personnaliser les publicités affichées. Elles ne sont jamais vendues à des tiers.'
          : 'Vous pourrez revenir sur ce choix à tout moment depuis votre espace Ventilio.'}
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
          Retour
        </button>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Enregistrement...' : 'Valider'}
        </button>
      </div>
    </form>
  );
};
